const IMESSAGE_CLAIM_FLOW_SESSION_STORAGE_KEY = "mem_imessage_claim_flow";
const IMESSAGE_CLAIM_COMPLETION_SURFACE_EXITED_SESSION_STORAGE_KEY =
  "mem_imessage_claim_completion_surface_exited";
const IMESSAGE_CLAIM_CHECKOUT_RETURN_TO_MESSAGES_SESSION_STORAGE_KEY =
  "mem_imessage_claim_checkout_return_to_messages";

let fallbackIMessageClaimFlowSessionIsActive = false;
let fallbackIMessageClaimCompletionSurfaceExited = false;
let fallbackIMessageClaimCheckoutReturnToMessagesIsPending = false;

const getSessionStorage = (browserWindow: Window): Storage | null => {
  try {
    return browserWindow.sessionStorage;
  } catch {
    return null;
  }
};

const setSessionStorageItem = ({
  browserWindow,
  key,
  value,
}: {
  browserWindow: Window;
  key: string;
  value: string;
}): void => {
  try {
    getSessionStorage(browserWindow)?.setItem(key, value);
  } catch {
    // The in-memory fallback keeps this tab's flow state usable when storage is blocked.
  }
};

const getSessionStorageItem = ({
  browserWindow,
  key,
}: {
  browserWindow: Window;
  key: string;
}): string | null => {
  try {
    return getSessionStorage(browserWindow)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const removeSessionStorageItem = ({
  browserWindow,
  key,
}: {
  browserWindow: Window;
  key: string;
}): void => {
  try {
    getSessionStorage(browserWindow)?.removeItem(key);
  } catch {
    // Storage removal can fail in private or restricted contexts; clearing memory is enough.
  }
};

/**
 * Marks the current browser tab as having entered Mem through an iMessage
 * connection claim link. This intentionally lives in sessionStorage so the
 * onboarding bypass stays local to this login tab instead of syncing to the
 * user's account.
 */
export const markIMessageClaimFlowSession = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): void => {
  fallbackIMessageClaimFlowSessionIsActive = true;

  setSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_FLOW_SESSION_STORAGE_KEY,
    value: "true",
  });
};

/**
 * Reads whether this tab came from an iMessage claim link. The in-memory
 * fallback keeps the bypass working when browser storage is unavailable.
 */
export const readIMessageClaimFlowSession = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): boolean => {
  const storedValue = getSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_FLOW_SESSION_STORAGE_KEY,
  });

  return storedValue === "true" || fallbackIMessageClaimFlowSessionIsActive;
};

/**
 * Marks that the iMessage claim completion page or upsell is no longer the
 * active surface. The claim-flow marker still stays active until logout so the
 * welcome overlay does not return after a refresh.
 */
export const markIMessageClaimCompletionSurfaceExited = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): void => {
  fallbackIMessageClaimCompletionSurfaceExited = true;

  setSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_COMPLETION_SURFACE_EXITED_SESSION_STORAGE_KEY,
    value: "true",
  });
};

export const readIMessageClaimCompletionSurfaceExited = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): boolean => {
  const storedValue = getSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_COMPLETION_SURFACE_EXITED_SESSION_STORAGE_KEY,
  });

  return storedValue === "true" || fallbackIMessageClaimCompletionSurfaceExited;
};

/**
 * Carries the iMessage claim context across Stripe checkout.
 *
 * The marker is consumed on checkout return so the Messages handoff modal can
 * appear once after a successful subscription, then stay gone after refresh.
 */
export const markIMessageClaimCheckoutReturnToMessagesPending = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): void => {
  fallbackIMessageClaimCheckoutReturnToMessagesIsPending = true;

  setSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_CHECKOUT_RETURN_TO_MESSAGES_SESSION_STORAGE_KEY,
    value: "true",
  });
};

export const clearIMessageClaimCheckoutReturnToMessagesPending = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): void => {
  fallbackIMessageClaimCheckoutReturnToMessagesIsPending = false;
  removeSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_CHECKOUT_RETURN_TO_MESSAGES_SESSION_STORAGE_KEY,
  });
};

export const consumeIMessageClaimCheckoutReturnToMessagesPending = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): boolean => {
  const storedValue = getSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_CHECKOUT_RETURN_TO_MESSAGES_SESSION_STORAGE_KEY,
  });
  const isPending =
    storedValue === "true" || fallbackIMessageClaimCheckoutReturnToMessagesIsPending;

  clearIMessageClaimCheckoutReturnToMessagesPending({ browserWindow });

  return isPending;
};

export const clearIMessageClaimFlowSession = ({
  browserWindow = window,
}: { browserWindow?: Window } = {}): void => {
  fallbackIMessageClaimFlowSessionIsActive = false;
  fallbackIMessageClaimCompletionSurfaceExited = false;
  fallbackIMessageClaimCheckoutReturnToMessagesIsPending = false;
  removeSessionStorageItem({ browserWindow, key: IMESSAGE_CLAIM_FLOW_SESSION_STORAGE_KEY });
  removeSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_COMPLETION_SURFACE_EXITED_SESSION_STORAGE_KEY,
  });
  removeSessionStorageItem({
    browserWindow,
    key: IMESSAGE_CLAIM_CHECKOUT_RETURN_TO_MESSAGES_SESSION_STORAGE_KEY,
  });
};
